import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Project } from "../interfaces/Project";
import { ensureProjectMapMarkerIcon, MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from "./projectMapLeaflet";
import { nominatimSearch } from "../api/nominatimGeocode";
import ProjectsAPI from "../api/projects";
import { getDefaultMapCenter, projectHasMapCoords } from "../utils/projectMapCoords";
import { MapPin, Search, Check, X } from "lucide-react";
import "./ProjectGeolocationModal.css";

export const PROJECT_RADIUS_MILES_MIN = 0.25;
export const PROJECT_RADIUS_MILES_MAX = 3;

export function clampProjectRadiusMiles(n: number): number {
    const c = Math.min(PROJECT_RADIUS_MILES_MAX, Math.max(PROJECT_RADIUS_MILES_MIN, n));
    return Math.round(c * 100) / 100;
}

function initialRadiusMiles(project: Project): number {
    const r = project.radiusMiles;
    if (typeof r === "number" && Number.isFinite(r) && r > 0) {
        return clampProjectRadiusMiles(r);
    }
    return 0.5; // prototype default for new locations
}

/** Fit marker + geofence in view. `fitKey` changes only when a re-fit is wanted
    (open / geocode jump) — NOT on slider ticks, so the circle resizes in place. */
function FitGeofence({ lat, lng, radiusMiles, fitKey }: { lat: number; lng: number; radiusMiles: number; fitKey: number }) {
    const map = useMap();
    useEffect(() => {
        const bounds = L.latLngBounds([[lat, lng]]);
        const milesPerDegLat = 69;
        const milesPerDegLng = 69 * Math.cos((lat * Math.PI) / 180);
        const dLat = radiusMiles / milesPerDegLat;
        const dLng = radiusMiles / milesPerDegLng;
        bounds.extend([lat + dLat, lng + dLng]);
        bounds.extend([lat - dLat, lng - dLng]);
        map.fitBounds(bounds.pad(0.15), { maxZoom: 16 });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- refit only on fitKey
    }, [map, fitKey]);
    return null;
}

function MapClickToMove({ enabled, onLatLng }: { enabled: boolean; onLatLng: (lat: number, lng: number) => void }) {
    useMapEvents({
        click(e) {
            if (!enabled) return;
            onLatLng(e.latlng.lat, e.latlng.lng);
        },
    });
    return null;
}

export interface ProjectGeolocationModalProps {
    isOpen: boolean;
    onClose: () => void;
    project: Project;
    canEdit: boolean;
    /** Which mode to open in. Defaults to "view" (or "edit" when the project has no coords and canEdit). */
    initialMode?: "view" | "edit";
    onSaved: (updated: Project) => void;
    onToast: (kind: "ok" | "err", msg: string) => void;
}

const ProjectGeolocationModal: React.FC<ProjectGeolocationModalProps> = ({
    isOpen,
    onClose,
    project,
    canEdit,
    initialMode,
    onSaved,
    onToast,
}) => {
    ensureProjectMapMarkerIcon();

    const hasCoords = projectHasMapCoords(project);
    const [mode, setMode] = useState<"view" | "edit">("view");
    const [draftLat, setDraftLat] = useState<number>(0);
    const [draftLng, setDraftLng] = useState<number>(0);
    const [draftRadius, setDraftRadius] = useState<number>(0.5);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchBusy, setSearchBusy] = useState(false);
    const [saving, setSaving] = useState(false);
    const [fitKey, setFitKey] = useState(0);

    // (Re)initialize on open.
    useEffect(() => {
        if (!isOpen) return;
        const startEdit = canEdit && (initialMode === "edit" || (!projectHasMapCoords(project) && initialMode !== "view"));
        setMode(startEdit ? "edit" : "view");
        if (projectHasMapCoords(project)) {
            setDraftLat(project.latitude as number);
            setDraftLng(project.longitude as number);
        } else {
            const c = getDefaultMapCenter();
            setDraftLat(c.lat);
            setDraftLng(c.lng);
        }
        setDraftRadius(initialRadiusMiles(project));
        setSearchQuery("");
        setSaving(false);
        setFitKey((k) => k + 1);
    }, [isOpen, project, canEdit, initialMode]);

    // Esc closes.
    useEffect(() => {
        if (!isOpen) return;
        const onEsc = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onEsc);
        return () => document.removeEventListener("keydown", onEsc);
    }, [isOpen, onClose]);

    const editing = mode === "edit" && canEdit;

    const onMapClick = useCallback((lat: number, lng: number) => {
        setDraftLat(lat);
        setDraftLng(lng);
    }, []);

    const handleSearch = async () => {
        const q = searchQuery.trim();
        if (!q || searchBusy) return;
        setSearchBusy(true);
        try {
            const result = await nominatimSearch(q);
            if (!result) {
                onToast("err", "No results found. Try a different address.");
                return;
            }
            setDraftLat(result.lat);
            setDraftLng(result.lon);
            setFitKey((k) => k + 1); // jump the map to the geocoded point
        } catch (err: any) {
            console.error(err);
            onToast("err", err?.message || "Geocoding failed. Try again.");
        } finally {
            setSearchBusy(false);
        }
    };

    const handleSave = async () => {
        if (saving) return;
        const lat = draftLat;
        const lng = draftLng;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
            onToast("err", "Invalid coordinates.");
            return;
        }
        const radiusMiles = clampProjectRadiusMiles(draftRadius);
        setSaving(true);
        try {
            const response = await ProjectsAPI.updateProjectGeolocation(project.projectID, {
                latitude: lat,
                longitude: lng,
                radiusMiles,
            });
            onSaved(response.data);
            onToast("ok", `Map location saved — ${lat.toFixed(5)}, ${lng.toFixed(5)} · ${radiusMiles.toFixed(2)} mi radius`);
            onClose();
        } catch (error: any) {
            const status = error?.response?.status;
            const msg = error?.response?.data?.message;
            onToast("err", status === 403
                ? (msg || "You are not allowed to edit project geolocation.")
                : (msg || "Failed to save project location."));
        } finally {
            setSaving(false);
        }
    };

    const lat = draftLat;
    const lng = draftLng;
    const coordsOk = Number.isFinite(lat) && Number.isFinite(lng);
    const displayRadius = Math.max(draftRadius, PROJECT_RADIUS_MILES_MIN);
    const viewRadius = useMemo(() => initialRadiusMiles(project), [project]);

    if (!isOpen) return null;

    return (
        <div className="pgeo-overlay" onClick={onClose} role="presentation">
            <div
                className="pgeo-modal"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="pgeo-title"
            >
                {/* header */}
                <div className="pgeo-head">
                    <div className="pgeo-hic">
                        <MapPin size={20} />
                    </div>
                    <div className="pgeo-ht">
                        <h3 id="pgeo-title">{editing ? "Set map location" : "Project geofence"}</h3>
                        <div className="pgeo-sub">
                            {editing
                                ? "Click the map (or search an address) to move the point, then set the radius."
                                : "Marker and geofence circle for this project."}
                        </div>
                    </div>
                    <button type="button" className="pgeo-x" onClick={onClose} aria-label="Close">
                        <X size={16} />
                    </button>
                </div>

                {/* body */}
                <div className="pgeo-body">
                    {editing && (
                        <div className="pgeo-frow">
                            <label>Search address</label>
                            <div className="pgeo-searchwrap">
                                <Search size={15} />
                                <input
                                    type="text"
                                    placeholder="Search an address (Nominatim)…"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            void handleSearch();
                                        }
                                    }}
                                    disabled={searchBusy}
                                />
                                {searchBusy && <span className="pgeo-spin" />}
                            </div>
                        </div>
                    )}

                    <div className={`pgeo-map${editing ? " editable" : ""}`}>
                        {coordsOk && (
                            <MapContainer
                                center={[lat, lng]}
                                zoom={14}
                                className="pgeo-leaflet"
                                scrollWheelZoom={true}
                            >
                                <TileLayer
                                    attribution={MAP_TILE_ATTRIBUTION}
                                    url={MAP_TILE_URL}
                                    maxZoom={MAP_TILE_MAX_ZOOM}
                                    referrerPolicy="strict-origin-when-cross-origin"
                                />
                                <FitGeofence lat={lat} lng={lng} radiusMiles={displayRadius} fitKey={fitKey} />
                                <MapClickToMove enabled={editing} onLatLng={onMapClick} />
                                <Circle
                                    center={[lat, lng]}
                                    radius={displayRadius * 1609.344}
                                    pathOptions={{
                                        color: "#2f6fed",
                                        fillColor: "#2f6fed",
                                        fillOpacity: 0.13,
                                        weight: 2,
                                    }}
                                />
                                <Marker
                                    position={[lat, lng]}
                                    draggable={editing}
                                    eventHandlers={
                                        editing
                                            ? {
                                                  dragend: (e) => {
                                                      const p = e.target.getLatLng();
                                                      setDraftLat(p.lat);
                                                      setDraftLng(p.lng);
                                                  },
                                              }
                                            : undefined
                                    }
                                />
                            </MapContainer>
                        )}
                        {editing && (
                            <div className="pgeo-hint">
                                <MapPin size={14} />
                                Click anywhere to drop the point
                            </div>
                        )}
                    </div>

                    {/* readout */}
                    <div className="pgeo-readout">
                        <div className="pgeo-coords">
                            <span>Lat <b>{coordsOk ? lat.toFixed(6) : "—"}</b></span>
                            <span>Lng <b>{coordsOk ? lng.toFixed(6) : "—"}</b></span>
                            {!editing && (
                                <span>Radius <b>{viewRadius.toFixed(2)} mi</b></span>
                            )}
                        </div>
                        {editing && (
                            <div className="pgeo-radius">
                                <span className="rc-lab">Radius</span>
                                <input
                                    type="range"
                                    min={PROJECT_RADIUS_MILES_MIN}
                                    max={PROJECT_RADIUS_MILES_MAX}
                                    step={0.25}
                                    value={displayRadius}
                                    onChange={(e) => setDraftRadius(clampProjectRadiusMiles(Number(e.target.value)))}
                                />
                                <span className="rc-val">{displayRadius.toFixed(2)} mi</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* footer */}
                <div className="pgeo-foot">
                    {editing ? (
                        <>
                            <button type="button" className="pgeo-btn ghost" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="pgeo-btn primary"
                                onClick={() => void handleSave()}
                                disabled={saving || !coordsOk}
                            >
                                <Check size={15} />
                                {saving ? "Saving…" : "Save location"}
                            </button>
                        </>
                    ) : (
                        <>
                            <button type="button" className="pgeo-btn ghost" onClick={onClose}>
                                Close
                            </button>
                            {canEdit && (
                                <button type="button" className="pgeo-btn primary" onClick={() => setMode("edit")}>
                                    <MapPin size={15} />
                                    Edit location
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectGeolocationModal;
